import { useMemo, type MutableRefObject } from 'react';

import { useCurrentTheme } from '@dynatrace/strato-components/core';

import { Canvas } from '../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../core/components/canvas/CanvasContext.js';
import { CanvasShape } from '../../core/components/canvas/types/canvas-shape.js';
import { plotLayout } from '../../core/components/chart-grid/ChartGrid.sty.js';
import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { OVERLAY_PADDING } from '../../core/components/data-point/constants.js';
import { AreaChartDisplay } from '../components/displays/area/AreaChartDisplay.js';
import { BandChartDisplay } from '../components/displays/band/BandChartDisplay.js';
import { BarChartDisplay } from '../components/displays/bar/BarChartDisplay.js';
import { DisplayConfigContext } from '../components/displays/DisplayConfigContext.js';
import { LineChartDisplay } from '../components/displays/line/LineChartDisplay.js';
import { datapointCanvasPlot } from '../components/overlays/TimeseriesOverlays.sty.js';
import { useHighlightedSeriesIdsState } from '../hooks/store/selectors.js';
import { useTimeseriesFilteredData } from '../hooks/useTimeseriesFilteredData.js';
import { useXScales } from '../hooks/useXScale.js';
import { ChartVariant } from '../types/timeseries-props.js';

const displays = {
  line: LineChartDisplay,
  bar: BarChartDisplay,
  area: AreaChartDisplay,
};

export const TimeseriesCanvasLayout = () => {
  const { currentScale: xScale } = useXScales();
  const { dataPerVariant } = useTimeseriesFilteredData();
  const { width, height } = useGridPlotSize();
  const highlightedSeriesIds = useHighlightedSeriesIdsState();
  const theme = useCurrentTheme();

  const displayConfigContext = useMemo(
    () => ({
      highlightedSeriesIds,
    }),
    [highlightedSeriesIds, theme],
  );

  return (
    <DisplayConfigContext.Provider value={displayConfigContext}>
      {dataPerVariant.shapePriority.map((variant, index) => {
        let Display:
          | typeof LineChartDisplay
          | typeof BarChartDisplay
          | typeof AreaChartDisplay = LineChartDisplay;

        if (variant !== 'band') {
          Display = displays[variant as ChartVariant];
        }

        const isDottedShape = variant === 'line' || variant === 'area';

        const canvasContainerClassName = isDottedShape
          ? datapointCanvasPlot
          : plotLayout;

        const canvasWidth =
          width && isDottedShape ? width + OVERLAY_PADDING : width;
        const canvasHeight =
          height && isDottedShape ? height + OVERLAY_PADDING : height;

        return (
          <div
            key={`display-${variant}${index}`}
            className={canvasContainerClassName}
          >
            <CanvasContext.Provider
              value={{
                shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
              }}
            >
              <Canvas width={canvasWidth} height={canvasHeight}>
                {variant === 'band' ? (
                  <BandChartDisplay
                    series={dataPerVariant.band}
                    xScale={xScale}
                  />
                ) : (
                  <Display
                    series={dataPerVariant[variant as ChartVariant]}
                    xScale={xScale}
                  />
                )}
              </Canvas>
            </CanvasContext.Provider>
          </div>
        );
      })}
    </DisplayConfigContext.Provider>
  );
};

TimeseriesCanvasLayout['displayName'] = 'TimeseriesCanvasLayout';
